import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {concat, forkJoin, Observable, of, Subscription} from "rxjs";
import {catchError, concatMap, distinctUntilChanged, filter, first, map} from "rxjs/operators";
import {MetaService} from "../../../../shared/meta/service/meta.service";
import {CoreMetaExternalSideCarService} from "../../../../shared/meta/service/core-meta-external-side-car.service";
import {MetaDataAttributeKind} from "../../../../shared/meta/logic/meta-data-attribute-kind";
import {
    QL_BUILDER_PRESETS,
    QlBuilderPresetResolver,
    QlBuilderPresetResult,
    resolveAllQlBuilderPresets,
} from "../../../../shared/ql/builder/ql-builder-presets";
import {UiQlWidgetWorkbenchStateService} from "../../../service/domain/ui-ql-widget-workbench-state.service";
import {UiQlResolvedSideCar} from "../../../interface/ui-ql-resolved-side-car.interface";
import {UiQlPresetEnvironment} from "../../../interface/ui-ql-preset-environment.interface";
import {UiQlPresetOption} from "../../../interface/ui-ql-preset-option.interface";

/**
 * Preset dropdown of the QL-Playground: `resolveAllQlBuilderPresets` → one click loads a preset;
 * presets that are not available on this environment are greyed out with the German reason.
 *
 * The preset resolver is synchronous (pure module); the environment lookups it needs (first
 * generic field of `xx.XxGenericTest`, first usable side-car with scope and pivotable fields) are
 * fetched LAZILY (N13: seven discovery requests on every playground load were pure overhead) - the
 * first time the dropdown opens, or on a selection made before that - and handed in as a lookup
 * table. Until then the list shows every preset as selectable; a selection made before the facts
 * are in is completed once they are. A failing lookup is "not available", never an error of the
 * whole dropdown.
 *
 * The selection is only a "load" action, not a state (N2): whenever the workbench content changes
 * by anything but this dropdown (reset, JSON, chat, a builder edit) the selection is cleared, so
 * the same preset can be loaded again.
 *
 * Declared in `UiQlWidgetBuilderModule` (NgModule, fe_core_standards) - its only host is
 * `UiQlWidgetWorkbenchPage` of the same module.
 */
@Component({
    selector: 'ui-ql-preset-select',
    templateUrl: './ui-ql-preset-select.component.html',
    styleUrls: ['./ui-ql-preset-select.component.scss'],
    standalone: false,
})
export class UiQlPresetSelectComponent implements OnInit, OnDestroy {

    /** Entities whose first generic field a preset needs (preset 5). */
    static readonly GENERIC_ENTITIES = ['xx.XxGenericTest'];

    /** Side-car candidates of preset 7, in preference order (see ql-builder-presets.ts). */
    static readonly SIDE_CAR_CANDIDATES = [
        {hostAlias: 'xx.XxPerson', instanceAlias: 'xx.XxPersonExternalFlat'},
        {hostAlias: 'cm.Contract', instanceAlias: 'ci.ContractExtData'},
    ];

    /** Scopes tried per side-car before giving up (each costs one `/fields` call). */
    static readonly MAX_SCOPES_TRIED = 3;

    /** Fields a preset takes from a scope. */
    static readonly MAX_PRESET_FIELDS = 5;

    /** An available preset was picked. */
    @Output() presetLoad = new EventEmitter<QlBuilderPresetResult>();

    /** `idle` = environment not asked yet (every preset offered), then `loading` → `ready` | `error`. */
    state: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
    options: UiQlPresetOption[] = [];
    selectedId: string | null = null;

    private presets: QlBuilderPresetResult[] = [];
    private subscription: Subscription | null = null;
    private revisionSubscription: Subscription | null = null;

    /** A preset picked while the environment was still loading - loaded as soon as the facts are in. */
    private pendingId: string | null = null;

    /** `true` while this dropdown itself pushes a preset into the state (its own revision bumps must not clear it). */
    private emitting: boolean = false;

    constructor(
        private readonly metaService: MetaService,
        private readonly sideCarService: CoreMetaExternalSideCarService,
        private readonly workbenchState: UiQlWidgetWorkbenchStateService) {
    }

    ngOnInit(): void {
        this.options = QL_BUILDER_PRESETS.map(preset => ({label: preset.title, value: preset.id, disabled: false, reason: null}));
        // N2: any content change not caused by this dropdown clears the selection
        this.revisionSubscription = this.workbenchState.state$.pipe(
            map(s => s.revision),
            distinctUntilChanged(),
        ).subscribe(() => {
            if (!this.emitting) {
                this.selectedId = null;
            }
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.revisionSubscription?.unsubscribe();
    }

    /** The dropdown opens: ask the environment once (N13). */
    ensureEnvironment(): void {
        if (this.state !== 'idle') {
            return;
        }
        this.state = 'loading';
        this.subscription = this.loadEnvironment().subscribe({
            next: environment => this.applyEnvironment(environment),
            error: () => {
                this.state = 'error';
                this.pendingId = null;
            },
        });
    }

    onSelect(id: string | null): void {
        this.selectedId = id;
        if (this.state !== 'ready') {
            // picked before the facts are in (keyboard, or the load is still running): finish once they are
            this.pendingId = id;
            this.ensureEnvironment();
            return;
        }
        this.loadPreset(id);
    }

    private loadPreset(id: string | null): void {
        const preset = this.presets.find(p => p.id === id);
        if (preset?.status !== 'available') {
            return;
        }
        this.emitting = true;
        try {
            this.presetLoad.emit(preset);
        } finally {
            this.emitting = false;
        }
    }

    /** Build the dropdown from the environment facts (public for tests). */
    applyEnvironment(environment: UiQlPresetEnvironment): void {
        this.presets = resolveAllQlBuilderPresets(UiQlPresetSelectComponent.createResolver(environment));
        this.options = this.presets.map(preset => ({
            label: preset.title,
            value: preset.id,
            disabled: preset.status !== 'available',
            reason: preset.status === 'unavailable' ? preset.reason : null,
        }));
        this.state = 'ready';
        const pending = this.pendingId;
        this.pendingId = null;
        if (pending != null) {
            if (this.presets.find(p => p.id === pending)?.status !== 'available') {
                this.selectedId = null; // turned out to be unavailable on this environment
            }
            this.loadPreset(pending);
        }
    }

    /** The synchronous resolver the preset module expects, answering from prefetched facts only. */
    static createResolver(environment: UiQlPresetEnvironment): QlBuilderPresetResolver {
        return {
            resolveGenericFieldId: entityAlias => environment.genericFieldIdByEntity[entityAlias] ?? null,
            resolveSideCar: candidates => {
                for (const candidate of candidates) {
                    const found = environment.sideCars[`${candidate.hostAlias}|${candidate.instanceAlias}`];
                    if (found) {
                        return found;
                    }
                }
                return null;
            },
        };
    }

    private loadEnvironment(): Observable<UiQlPresetEnvironment> {

        const generic$ = forkJoin(UiQlPresetSelectComponent.GENERIC_ENTITIES.map(alias =>
            this.resolveGenericFieldId(alias).pipe(map(id => [alias, id] as [string, number | null]))));

        const sideCars$ = forkJoin(UiQlPresetSelectComponent.SIDE_CAR_CANDIDATES.map(candidate =>
            this.resolveSideCar(candidate.hostAlias, candidate.instanceAlias)));

        return forkJoin([generic$, sideCars$]).pipe(
            map(([generics, sideCars]) => {
                const environment: UiQlPresetEnvironment = {genericFieldIdByEntity: {}, sideCars: {}};
                generics.forEach(([alias, id]) => environment.genericFieldIdByEntity[alias] = id);
                sideCars.filter(sc => !!sc).forEach(sc => environment.sideCars[`${sc.hostAlias}|${sc.instanceAlias}`] = sc);
                return environment;
            }),
        );
    }

    private resolveGenericFieldId(entityAlias: string): Observable<number | null> {
        return this.metaService.getByAlias(entityAlias, true, false, false).pipe(
            map(meta => {
                // attributeType is the enum ORDINAL on /meta/byAlias - the reader decides, never a string compare
                const attribute = (meta?.attributes ?? []).find(a => MetaDataAttributeKind.isGenericAttribute(a));
                return attribute ? MetaDataAttributeKind.genericAttributeIdOf(attribute) : null;
            }),
            catchError(() => of(null)),
        );
    }

    /**
     * host → discovery row of the instance → its scopes, tried in order until one has pivotable
     * fields. `null` whenever anything along the way is missing or fails.
     */
    private resolveSideCar(hostAlias: string, instanceAlias: string): Observable<UiQlResolvedSideCar | null> {
        return this.sideCarService.getSideCarsForHost(hostAlias).pipe(
            map(rows => (rows ?? []).find(row => row.instanceAlias === instanceAlias || row.joinName === instanceAlias) ?? null),
            concatMap(row => {
                if (!row) {
                    return of(null);
                }
                return this.sideCarService.getScopes(row.instanceAlias).pipe(
                    concatMap(scopes => {
                        const tried = (scopes ?? []).slice(0, UiQlPresetSelectComponent.MAX_SCOPES_TRIED);
                        if (!tried.length) {
                            return of(null);
                        }
                        const perScope = tried.map(scope => this.sideCarService.getFields(row.instanceAlias, scope.id).pipe(
                            map(fields => {
                                const ids = (fields ?? [])
                                    .filter(f => f.pivotable === true && f.dataType != null)
                                    .map(f => f.typeFieldId)
                                    .slice(0, UiQlPresetSelectComponent.MAX_PRESET_FIELDS);
                                return ids.length ? {
                                    hostAlias,
                                    instanceAlias: row.joinName || row.instanceAlias,
                                    scopeKey: row.scopeKey,
                                    scopeId: scope.id,
                                    typeFieldIds: ids,
                                } as UiQlResolvedSideCar : null;
                            }),
                            catchError(() => of(null)),
                        ));
                        // sequential, stop at the first scope with fields
                        return concat(...perScope).pipe(
                            filter(found => !!found),
                            first(null, null),
                        );
                    }),
                );
            }),
            catchError(() => of(null)),
        );
    }
}
